import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/core/auth/auth.service';
import { ToasterService } from 'app/services/toaster.service';
import { BontonPanel } from 'environments/bonton-panel';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';

/**
 * Password regex pattern for validation
 * Requires: 1 uppercase, 1 lowercase, 1 number, 1 special character, minimum 8 characters
 */
const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;

@Component({
    selector: 'app-set-password',
    standalone: true,
    imports: [
    FuseAlertComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatOptionModule,
    MatSelectModule,
    MatDividerModule,
    NgxMatSelectSearchModule
],
    templateUrl: './set-password.component.html',
})
export class SetPasswordComponent implements OnInit, OnDestroy {
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    setPassForm: UntypedFormGroup;
    showAlert: boolean = false;
    
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Initializes the SetPasswordComponent with required services and dialog data.
     * @param matDialogRef Reference to the active Material dialog instance.
     * @param alertService Service for displaying toast notifications.
     * @param _formBuilder Form builder for creating reactive forms.
     * @param _router Router service for navigation.
     * @param _authService Authentication service for password operations.
     * @param data Injected dialog data (optional).
     */
    constructor(
        public matDialogRef: MatDialogRef<SetPasswordComponent>,
        private alertService: ToasterService,
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private _authService: AuthService,
        @Inject(MAT_DIALOG_DATA) public data: any = []
    ) {
    }

    /**
     * On init - Initialize the password form
     */
    ngOnInit(): void {
        this._initializeForm();
    }

     /**
     * Initialize the password form with validators
     */
    _initializeForm(): void {
        this.setPassForm = this._formBuilder.group({
            password: ['', [Validators.required, Validators.minLength(8)]],
            passwordConfirm: ['', [Validators.required, confirmPasswordValidator]],
        });

        // Update password confirmation validity when password changes
        this.setPassForm.get('password').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.setPassForm.get('passwordConfirm').updateValueAndValidity();
            });
    }

    /**
     * Validate password against pattern requirements
     * @param password - Password string to validate
     * @returns True if password meets requirements
     */
    isPasswordValid(password: string): boolean {
        return PASSWORD_PATTERN.test(password);
    }

    /**
     * Submit the new password
     */
    setPass(): void {
        Object.keys(this.setPassForm.controls).forEach(key => {
            const control = this.setPassForm.get(key);
            if (control && typeof control.value === 'string') {
                control.setValue(control.value.trim());
            }
        });
        if (this.setPassForm.invalid) {
            this.alertService.showToast('error', 'The validation error needs to be corrected.');
            return;
        }

        this.showAlert = false;

        const json = {
            id: BontonPanel.isB2C() ? this.data.id : '',
            password: this.setPassForm.get('password').value
        }

        const serviceCall = BontonPanel.isB2C()
            ? this._authService.b2csetPassword(json)
            : this._authService.setPassword(json);

        serviceCall
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.matDialogRef.close();
                    this._router.navigate(['/sign-out']);
                    Swal.fire('Password Updated!', 'Kindly proceed to log in with your new password.', 'success')
                },
                error: (err) => {
                    this.alertService.showToast('error', err);
                }
            });
    }

    /**
     * Sign out the user and close dialog
     */
    signOut(): void {
        this.matDialogRef.close();
        this._router.navigate(['/sign-out']);
    }

    /**
     * On destroy - Clean up subscriptions
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}

/**
 * Confirm password validator
 * Validates that password and confirm password fields match
 * 
 * @param control - The form control to validate
 * @returns ValidationErrors object if passwords don't match, null otherwise
 */
export const confirmPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control) {
        return null;
    }

    const password = control.parent.get('password');
    const passwordConfirm = control.parent.get('passwordConfirm');

    if (!password || !passwordConfirm) {
        return null;
    }

    if (passwordConfirm.value === '') {
        return null;
    }

    if (password.value === passwordConfirm.value) {
        return null;
    }

    return { passwordsNotMatching: true };
};